import { Component, EventEmitter, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take, takeUntil } from 'rxjs/operators';
import { DeactivationDialogService } from '../../../helper/can-deactivate/deactivation-dialog.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { Product, ProductType } from '../../../models/project/plant-specifications/product.model';
import { DigesterType } from '../../../models/project/plant-specifications/plant-specifications.model';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-edit-product-dialog',
  templateUrl: './edit-product-dialog.component.html',
  styleUrls: ['./edit-product-dialog.component.scss']
})
export class EditProductDialogComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @ViewChild('productForm') form: NgForm;

  onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger: string;

  model: Product = null;
  genProds: Product[] = [];
  originalName: string;
  isEdit = false;

  pickedGenerated: { name: string, productType: ProductType } = null;

  productOptions = [
    { name: 'PRODUCTS.ELECTRICITY', productType: ProductType.Electric },
    { name: 'PRODUCTS.THERMAL', productType: ProductType.Heat },
    { name: 'PRODUCTS.BIOMETHANE', productType: ProductType.Biometh },
    { name: 'PRODUCTS.WDBF', productType: ProductType.WholeDigestate },
    { name: 'PRODUCTS.TDBF', productType: ProductType.ThinDigestate },
    { name: 'PRODUCTS.SDBF', productType: ProductType.SolidDigestate }
  ];

  showClearPickedGenerated = false;
  showClearName = false;
  showClearDesc = false;

  constructor(
    private toastrService: ConfiguredToastrService,
    private dialogRef: MatDialogRef<EditProductDialogComponent>,
    private dialogService: DeactivationDialogService,
    public translationService: AnessaTranslationService,
    @Inject(MAT_DIALOG_DATA) public data: {
      product: Product,
      products: Product[],
      isEdit: boolean,
      digester: string,
      hasDewatering: boolean
    },
  ) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.isEdit = this.data.isEdit;
    if (this.isEdit) {
      this.model = this.data.product;
      this.originalName = this.data.product.name;
      if (this.model.internalDemand.unit === null && this.model.internalDemand.value === null) {
        this.model.internalDemand.unit = '000000000000000000000401';
      }
    } else {
      this.model = new Product();
      this.model.internalDemand.unit = '000000000000000000000401';
    }

    this.genProds = this.data.products;
    // only allow one of each product to be selected
    this.pickedGenerated = this.productOptions.find(prod => prod.productType === this.model.type);

    this.dialogRef.backdropClick().subscribe(() => {
      if (this.form.dirty) {
        this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'))
          .pipe(take(1)).subscribe(result => {
          if (result) {
            this.dialogRef.close();
          }
        });
      } else {
        this.dialogRef.close();
      }
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * clears the input that is using the provided property, assumes it is
   * part of the availability variable
   * @param property: the availability variable property name that is to be cleared
   */
  clearInput(property: string) {
    const newProduct = new Product();
    this.model[property] = newProduct[property];
    this.form.form.markAsDirty();
  }

  onSave() {
    if (this.isDuplicateName(this.model.name)) {
      this.toastrService.error(this.translationService.get('PROJECT.PLANT_SPEC.DUPE_P_NAME'));
      return;
    }
    this.dialogRef.close(this.model);
  }

  onCancel() {
    this.dialogRef.close();
  }

  /**
   * Disables possible product options based on certain conditions.
   * @param option product option name
   */
  allowProduct(option) {
    switch (option) {
      // continuous digester w/o dewatering = whole
      case ProductType.WholeDigestate:
        if (!this.prodExists(option) && (!this.data.hasDewatering && this.data.digester !== DigesterType.DRY_BATCH)) return true;
        return false;
      // continuous digester w/ dewatering = thin
      case ProductType.ThinDigestate:
        if (!this.prodExists(option) && (this.data.hasDewatering && this.data.digester !== DigesterType.DRY_BATCH)) return true;
        return false;
      // continuous digester w/ dewatering || dry batch = solid
      case ProductType.SolidDigestate:
        if (!this.prodExists(option) && (this.data.hasDewatering || this.data.digester === DigesterType.DRY_BATCH)) return true;
        return false;
      case ProductType.Electric:
        return !this.prodExists(option);
      case ProductType.Heat:
        return !this.prodExists(option);
      case ProductType.Biometh:
        return !this.prodExists(option);
      default:
        return true;
    }
  }

  internalUseProduct() {
    if (this.pickedGenerated) {
      if (this.pickedGenerated.productType === ProductType.SolidDigestate
        || this.pickedGenerated.productType === ProductType.ThinDigestate
        || this.pickedGenerated.productType === ProductType.WholeDigestate
        || this.pickedGenerated.productType === ProductType.Electric
        || this.pickedGenerated.productType === ProductType.Heat
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks for duplicate product names.
   * @param name poduct name
   */
  isDuplicateName(name: string) {
    let isDuplicateName = false;
    for (const prod of this.genProds) {
      if (prod.name === name && name !== this.originalName) {
        isDuplicateName = true;
      }
    }
    return isDuplicateName;
  }

  /**
   * By default, set the product name to the selected product type.
   */
  setName() {
    if (this.pickedGenerated) {
      this.model.name = this.translationService.get(this.pickedGenerated.name);
    }
    this.model.type = this.pickedGenerated.productType;
  }

  getUnitString(): string[] {
    const units = ['Percentage_Only'];
    switch (this.model.type) {
      case ProductType.Electric:
        units.push('Energy');
        break;
      case ProductType.Heat:
        units.push('Thermal_Quantity');
        break;
      case ProductType.Biometh:
        units.push('Volume_Gas');
        break;
      case ProductType.SolidDigestate:
        units.push('Mass');
        break;
      case ProductType.WholeDigestate:
      case ProductType.ThinDigestate:
        units.push(...['Mass', 'Volume_Liquid']);
    }
    return units;
  }

  getDisableMessage(prodType: string) {
    switch (prodType) {
      // continuous digester w/o dewatering = whole
      case ProductType.WholeDigestate:
        if (this.prodExists(prodType)) {
          return this.translationService.get('PROJECT.PLANT_SPEC.1WD');
        }
        if (this.data.hasDewatering || this.data.digester === DigesterType.DRY_BATCH) {
          return this.translationService.get('PROJECT.PLANT_SPEC.NO_WD');
        }
        break;
      // continuous digester w/ dewatering = thin
      case ProductType.ThinDigestate:
        if (this.prodExists(prodType)) {
          return this.translationService.get('PROJECT.PLANT_SPEC.1TD');
        }
        if (!this.data.hasDewatering || this.data.digester === DigesterType.DRY_BATCH) {
          return this.translationService.get('PROJECT.PLANT_SPEC.NO_WD');
        }
        break;
      // continuous digester w/ dewatering || dry batch = solid
      case ProductType.SolidDigestate:
        if (this.prodExists(prodType)) {
          return this.translationService.get('PROJECT.PLANT_SPEC.1SD');
        }
        if (!this.data.hasDewatering && this.data.digester !== DigesterType.DRY_BATCH) {
          return this.translationService.get('PROJECT.PLANT_SPEC.NO_SD');

        }
        break;
      case ProductType.Electric:
        return this.translationService.get('PROJECT.PLANT_SPEC.1E');
      case ProductType.Heat:
        return this.translationService.get('PROJECT.PLANT_SPEC.1T');
      case ProductType.Biometh:
        return this.translationService.get('PROJECT.PLANT_SPEC.1B');
    }
  }

  private prodExists(type: string) {
    if (this.genProds && this.genProds.length > 0) {
      for (const item of this.genProds) {
        if (item.type === type) {
          return true;
        }
      }
    }
  }

  validateName() {
    if (this.model.name.includes('.')) {
      const control: AbstractControl = this.form['form'].controls['name'];
      if (!control) { return; }
      control.setErrors({'incorrect-input': true});
      setTimeout(() => {
        control.markAsTouched();
      });
    }
  }
}
